import type { SeverityLevel } from "@sentry/react";
import { isDocEmpty } from "@smartsuite/smartdoc";
import type { Doc, EditorSchema } from "@smartsuite/smartdoc";

import {
  getSanitizeDocReasons,
  SanitizeDocReport,
  SanitizeDocResult,
  toRenderableDoc,
  unchangedSanitizeDocResult,
} from "./sanitizeDoc";

/**
 * Reconcile the document the native app injected with the editor schema - but only when
 * the editor is going to parse it.
 *
 * The editor checks `isDocEmpty` first and starts from a blank document when it is true,
 * so an "empty" document is never parsed and cannot crash anything. Several of the shapes
 * it treats as empty are not valid ProseMirror JSON all the same - an empty field is
 * stored as a paragraph holding a zero-length text node - and reconciling those produced
 * a report for every empty field opened on mobile. Skip them, exactly as the editor does.
 */
export const toRenderableIncomingDoc = (
  doc: Doc | undefined,
  schema: EditorSchema | undefined
): SanitizeDocResult =>
  !doc || isDocEmpty(doc) ? unchangedSanitizeDocResult(doc) : toRenderableDoc(doc, schema);

export interface IncomingDocMismatch {
  error: Error;
  level: SeverityLevel;
  /** Type names and counts only - the document body is customer content. */
  data: SanitizeDocReport;
  tags: Record<string, string>;
  fingerprint: string[];
}

/**
 * Turn a reconciliation that changed something into a Sentry report that can be triaged:
 * the reasons are in the title, in an indexed tag, and in the fingerprint - so a
 * lossless repair and a discarded document stop sharing one issue and one count.
 */
export const describeIncomingDocMismatch = (result: SanitizeDocResult): IncomingDocMismatch => {
  const reasons = getSanitizeDocReasons(result.report);

  return {
    error: new Error(
      `The incoming document does not match the editor schema: ${reasons.join(", ")}`
    ),
    // A repaired document still renders; a discarded one is content the user cannot see.
    level: result.doc === undefined ? "error" : "warning",
    data: result.report,
    tags: { doc_issue: reasons.join(",") },
    fingerprint: ["incoming-doc-schema-mismatch", ...reasons],
  };
};
